import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemText,
  Typography,
  Badge,
  Stack,
  AccordionSummary,
  Accordion,
  AccordionDetails,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useGetIframesByParamsQuery } from "../Slices/roomNateConstantApis";
import { useGlobalContext } from "../context/GlobalContext";
import LoaderSpinner from "./LoaderSpinner";
import { getFabCircleGlassSx, ink } from "../theme/spatial";

const slideWidth = 128;
const marginRight = 10;

const ShoppingList = ({
  items,
  index,
  containerRef,
  furniture,
  setCartItems,
  cartItems,
}) => {

  const { style } = useGlobalContext();
  const [selectedPriceFilter] = useState("$");
  const [filterStateData, setFilterStateData] = useState({});

  const [selectedItem, setSelectedItem] = useState(null);
  const [lItemsArray, setLItemsArray] = useState([]);
  const [ids, setIds] = useState([]);

  function sortShoppingList(items) {
    const itemsList = items?.length >= 1 ? items : [];
    return [...itemsList]?.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      const isNumA = /^\d/.test(nameA); 
      const isNumB = /^\d/.test(nameB);

      if (isNumA && !isNumB) return -1; 
      if (!isNumA && isNumB) return 1; 

      return nameA.localeCompare(nameB, undefined, { numeric: true }); 
    });
  }

  const accordionRefs = useRef([]);

  const { data, refetch, isFetching } = useGetIframesByParamsQuery({
    ...(ids.length > 0 ? { items: ids } : {}),
    ...(style ? { style } : {}),
  });

  useEffect(() => {
    const cleanedFurnitureId = furniture?.substring(
      furniture?.indexOf("-") + 1
    );

    if (items?.[index]?.furniture) {
      const newLItemsArray = [];

      const specificFurniture = items[index].furniture.find(
        (item) => item._id === cleanedFurnitureId
      );

      if (specificFurniture) {
        const lItemsToProcess =
          specificFurniture?.lItems?.length > 0
            ? specificFurniture.lItems
            : [specificFurniture];

        lItemsToProcess?.forEach((lItem) => {
          const baseId = lItem?._id;
          const capacity =
            lItem.capacity && !isNaN(lItem.capacity) ? +lItem.capacity : 1;

          const existingItemIndex = newLItemsArray.findIndex(
            (existing) => existing._id === baseId
          );

          if (existingItemIndex !== -1) {
            newLItemsArray[existingItemIndex].capacity += capacity;
          } else {
            newLItemsArray.push({
              _id: baseId,
              name: lItem?.name || "No Name",
              frontViewImage: lItem?.frontViewImage || "no image",
              capacity,
              length: lItem?.height || 1,
              width: lItem?.width || 1,
            });
          }
        });
      } else {
        items[index].furniture.forEach((item) => {
          const lItemsToProcess =
            item?.lItems?.length > 0 ? item.lItems : [item];

          lItemsToProcess?.forEach((lItem) => {
            const baseId = lItem?._id;
            const capacity =
              lItem.capacity && !isNaN(lItem.capacity) ? +lItem.capacity : 1;

            const existingItemIndex = newLItemsArray.findIndex(
              (existing) => existing._id === baseId
            );

            if (existingItemIndex !== -1) {
              newLItemsArray[existingItemIndex].capacity += capacity;
            } else {
              newLItemsArray.push({
                _id: baseId,
                name: lItem?.name || "No Name",
                frontViewImage: lItem?.frontViewImage || "no image",
                capacity,
                length: lItem?.height || 1,
                width: lItem?.width || 1,
              });
            }
          });
        });
      }

      setLItemsArray(sortShoppingList(newLItemsArray));
    }
  }, [items, index, furniture]);

  useEffect(() => {
    const flattenedIds =
      (items?.[index] &&
        items[index].furniture?.flatMap((item) =>
          item?.lItems?.length > 0
            ? item.lItems.map((lItem) => lItem._id)
            : [item._id]
        )) ||
      [];

    const extractUniqueIds = (ids) => {
      const uniqueIds = new Set();
      ids.forEach((id) => {
        if (typeof id !== "string" || id.length === 0) return;
        const baseId = id.split("-")[0];
        if (!baseId) return;
        uniqueIds.add(baseId);
      });
      return Array.from(uniqueIds); 
    };

    const uniqueIds = extractUniqueIds(flattenedIds);

    setIds(uniqueIds);
  }, [items, index]);

  useEffect(() => {
    refetch();
  }, [ids, refetch]);

  const updateQuantity = (operation, iframe, index) => {
    setCartItems((prevCartItems) => {
      const newCartItems = [...prevCartItems];
      const itemIndex = newCartItems.findIndex(
        (item) => item._id === iframe._id
      );

      if (operation === "increase") {
        if (itemIndex === -1) {
          newCartItems.push({
            _id: iframe._id,
            quantity: 1,
            amazonUrl: iframe?.amazonFields?.amazonUrl,
          });
        } else {
          newCartItems[itemIndex].quantity += 1;
        }
      } else if (operation === "decrease") {
        if (itemIndex === -1) {
          setFilterStateData((prevState) => {
            const newState = { ...prevState };
            if (newState[index]) {
              newState[index] = newState[index].filter(
                (item) => item._id !== iframe._id
              );
            }
            return newState;
          });
        } else {
          const newQuantity = newCartItems[itemIndex].quantity - 1;
          if (newQuantity > 0) {
            newCartItems[itemIndex].quantity = newQuantity;
          } else {
            newCartItems.splice(itemIndex, 1);
          }
        }
      }

      return newCartItems;
    });
  };

  const generateCartUrl = (cartItems) => {
    const associateTag = "roomalgo-20"; 
    let url = `http://www.amazon.com/gp/aws/cart/add.html?AssociateTag=${associateTag}&`;

    cartItems.forEach((item, index) => {
      const asin = item.amazonUrl?.match(/\/dp\/([A-Z0-9]{10})/)?.[1];

      if (asin) {
        url += `ASIN.${index + 1}=${asin}&Quantity.${index + 1}=${
          item.quantity
        }&`;
      }
    });

    url = url.slice(0, -1);

    return url;
  };

  const handleOpenCart = () => {
    const cartUrl = generateCartUrl(cartItems);
    window.open(cartUrl, "_blank");
  };

  const filteredIframes = React.useCallback((dynamicItemId) =>
    data?.filter((iframe) => {
      const matchesItem = iframe.items.some(
        (item) => item._id === dynamicItemId
      );

      if (!selectedPriceFilter || !matchesItem) return matchesItem;

      return matchesItem;
    }), [data, selectedPriceFilter]);

  const calculatePercentiles = React.useCallback((data) => {
    if (data?.length < 1) {
      return;
    }
    const prices = data
      ?.map((item) => item.amazonFields.price ?? 0)
      ?.sort((a, b) => a - b);

    const percentile33 = prices?.[Math.floor((prices.length * 33) / 100)];
    const percentile66 = prices?.[Math.floor((prices.length * 66) / 100)];
    const percentile100 = prices?.[prices.length - 1];

    return { percentile33, percentile66, percentile100 };
  }, []);

  const filterDataByPrice = React.useCallback((data, filter) => {
    if (data?.length < 1) {
      return;
    }
    const { percentile33, percentile66 } =
      calculatePercentiles(data);

    let filteredData = [];

    if (filter === "$") {
      filteredData = data.filter(
        (item) => (item.amazonFields.price ?? 0) <= percentile33
      );
    } else if (filter === "$$") {
      filteredData = data.filter(
        (item) =>
          (item.amazonFields.price ?? 0) > percentile33 &&
          (item.amazonFields.price ?? 0) <= percentile66
      );
    } else if (filter === "$$$") {
      filteredData = data.filter(
        (item) => (item.amazonFields.price ?? 0) > percentile66
      );
    }

    return filteredData;
  }, [calculatePercentiles]);

  useEffect(() => {
    const updatedState = lItemsArray?.reduce((acc, item, index) => {
      const dynamicItemId = item._id;
      const filteredData = filteredIframes(dynamicItemId);
      const filteredDataPrice = filterDataByPrice(
        filteredData || [],
        selectedPriceFilter
      );

      acc[index] = filteredDataPrice;
      return acc;
    }, {});

    setFilterStateData(updatedState);
  }, [lItemsArray, data, selectedPriceFilter, filteredIframes, filterDataByPrice]); 

  const handleAccordionChange = (index) => {
    setSelectedItem((prev) => (prev === index ? null : index));

    setTimeout(() => {
      const element = accordionRefs.current[index];
      const container = containerRef.current; 

      if (element && container) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const scrollOffset =
          rect.top - containerRect.top + container.scrollTop - 50; 

        container.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    }, 500);
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <List
          sx={{
            py: 0,
            position: "relative",
          }}
        >
          {lItemsArray?.map((item, index) => {
            const filteredDataPrice = filterStateData[index] || [];
            const currentUrl = window.location.href;

            const parts = currentUrl?.split("//")[1]?.split("/");

            const domain = parts?.[0];

            const domains = [
              "serhant-dev.magicfurnish.com",
              "serhant.magicfurnish.com",
            ];

            return (
              <div
                key={index}
                style={{
                  width: "100%",
                  borderBottom:
                    selectedItem === index ? "1px solid #e0e0e0" : "none",
                }}
                ref={(el) => (accordionRefs.current[index] = el)}
              >
                <Accordion
                  expanded={selectedItem === index}
                  disableGutters
                  sx={{
                    boxShadow: "none",
                    backgroundColor: "transparent",
                  }}
                >
                  <AccordionSummary
                    aria-controls={`panel${selectedItem}-content`}
                    id={`panel${selectedItem}-content`}
                    expandIcon={
                      selectedItem ===
                      index ? null : 
                      domains.includes(domain) ? null : isFetching ? (
                        <LoaderSpinner text={false} />
                      )  : filteredDataPrice?.length === 0 ? null : (
                        <Button
                          variant="text"
                          disableRipple
                          sx={{
                            textTransform: "none",
                            color: ink.primary,
                            p: 0,
                            "& .MuiButton-endIcon": {
                              marginLeft: "4px",
                            },
                          }}
                          endIcon={<ExpandMore />}
                          onClick={() => handleAccordionChange(index)}
                        >
                          Shop
                        </Button>
                      )
                    }
                    sx={{
                      borderBottom:
                        selectedItem !== index ? "1px solid #e0e0e0" : "none",
                      padding: "8px 0",
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        transform: "rotate(0deg) !important",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          flexDirection="row"
                          alignItems="center"
                          gap={2}
                          justifyContent="space-between"
                        >
                          <>
                            <Typography
                              variant="body1"
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "19.6px",
                                textAlign: "left",
                              }}
                            >
                              {item.name}
                            </Typography>
                          </>
                        </Stack>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#20212480",
                            fontSize: "12px",
                            textAlign: "left",
                          }}
                        >
                          Qty: {item.capacity || 1}
                        </Typography>
                      }
                    />
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "12px",
                        overflowX: "auto",
                        padding: "0px 0px 16px 0px",
                        whiteSpace: "nowrap",
                        scrollbarWidth: "none", 
                        "&::-webkit-scrollbar": {
                          display: "none", 
                        },
                        msOverflowStyle: "none", 
                      }}
                    >
                      {filteredDataPrice.length === 0 ? (
                        <Typography variant="body2">
                          No Iframe exists with this filter.
                        </Typography>
                      ) : (
                        filteredDataPrice.map((iframe, idx) => (
                          <div
                            key={iframe._id || iframe.id || `iframe-${idx}`}
                            style={{
                              width: `${slideWidth}px`,
                              marginRight:
                                idx !== data?.length - 1
                                  ? `${marginRight}px`
                                  : 0,
                              pointerEvents: false ? "none" : "auto",
                            }}
                          >
                            <iframe
                              srcDoc={`<html><head><style>body { margin: 0; }</style></head><body>${iframe.amazonFields.amazonIframe}</body></html>`}
                              title="Embedded Content"
                              frameBorder="0"
                              sandbox="allow-popups allow-scripts allow-modals allow-forms allow-same-origin"
                              style={{
                                width: `${slideWidth}px`,
                                height: "235px",
                                pointerEvents: false ? "none" : "auto",
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                marginTop: "10px",
                                mx: 1,
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                disableRipple
                                sx={{
                                  padding: "8px",
                                  minWidth: "34px",
                                  height: "34px",
                                  borderRadius: "50%",
                                  backgroundColor: "#EBEDF0",
                                  color: "#FF5151",
                                  "&:hover": {
                                    background: "#EBEDF0",
                                  },
                                }}
                                onClick={() =>
                                  updateQuantity("decrease", iframe, index)
                                }
                              >
                                -
                              </Button>

                              {cartItems?.find(
                                (item) => item._id === iframe?._id
                              ) && (
                                <Typography>
                                  {cartItems.find(
                                    (item) => item._id === iframe?._id
                                  )?.quantity || ""}
                                </Typography>
                              )}

                              <Button
                                disableRipple
                                sx={{
                                  padding: "8px",
                                  minWidth: "34px",
                                  height: "34px",
                                  borderRadius: "50%",
                                  backgroundColor: "black",
                                  color: "white",
                                  "&:hover": {
                                    background: "black",
                                  },
                                }}
                                onClick={() =>
                                  updateQuantity("increase", iframe, index)
                                }
                              >
                                +
                              </Button>
                            </Box>
                          </div>
                        ))
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </List>
       
        {cartItems?.reduce((total, item) => total + item.quantity, 0) >= 1 && (
          <IconButton
            disabled={
              cartItems?.reduce((total, item) => total + item.quantity, 0) === 0
            }
            onClick={() => handleOpenCart()}
            size="small"
            sx={{
              position: "sticky",
              left: "100%",
              bottom: "0px",
              padding: "15px",
              ...getFabCircleGlassSx({ size: 44 }),
            }}
          >
            <Badge
              badgeContent={cartItems?.reduce(
                (total, item) => total + item.quantity,
                0
              )}
              color="primary"
              sx={{
                position: "absolute",
                top: 5,
                color: "white !important",
                right: 5,
              }}
            />

            <ShoppingCartOutlinedIcon sx={{ color: ink.fabLabel }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default ShoppingList;
